// Deterministic replays and one small simulation for Lesson 4.13 ("Memory Beyond One
// Conversation, and a Knowledge Base You Can Run"). The replies, the ingest line and the recall
// table were recorded on 9 October 2026 with zai.glm-5 through the gateway on the module dataset
// with the notes ingested; the recall graphic simulates a filter over four stored rows. Nothing
// here calls a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const tokensText = (tokens: number) => tokens.toLocaleString('en-US');

// --- memory-rows: one line the assistant wrote, read back in a new conversation ------------

export const MEMORIES = ['none', 'line'] as const;
export type Memory = (typeof MEMORIES)[number];
export const memoryLabels: Record<Memory, string> = {
  none: 'none',
  line: 'one line written from the first conversation',
};
export const toMemory = (value: string): Memory => MEMORIES.find((id) => id === value) ?? 'none';
export const FIRST_MESSAGE =
  'From now on call me M, and when you list tasks, put the task id first. What is open in Apollo?';
export const FIRST_REPLY =
  'M, here are the open tasks in Apollo: 9 Write the release note (Ravi), 10 Check the dates, …';
export const NEW_QUESTION = 'List the open Apollo tasks.';
export const MEMORY_LINE =
  'What you remember about this person, written down earlier: they want to be called M; when listing tasks, put the task id first.';
export const DEFAULT_REPLY =
  'Here are the open tasks in Apollo: Write the release note (assigned to Ravi), Check the dates, …';
export const RECALLED_REPLY =
  '- 9: Write the release note (assigned to Ravi) - 10: Check the dates …';
export interface MemoryRun {
  systemLine: string | null;
  reply: string;
  idsFirst: boolean;
  calledM: boolean;
  rows: number;
  message: string;
}
export const memoryRuns: Record<Memory, MemoryRun> = {
  none: {
    systemLine: null,
    reply: DEFAULT_REPLY,
    idsFirst: false,
    calledM: false,
    rows: 0,
    message:
      'The preference died with the first conversation, so the list came back in the default form.',
  },
  line: {
    systemLine: MEMORY_LINE,
    reply: RECALLED_REPLY,
    idsFirst: true,
    calledM: false,
    rows: 1,
    message: 'One recalled line brought the ids back, and the name was still not used.',
  },
};
export const yesNo = (value: boolean) => (value ? 'yes' : 'no');
export const systemText = (run: MemoryRun) => run.systemLine ?? '(no memory line)';
export interface RowsState {
  memory: Memory;
  asked: boolean;
}
export const initialRows = (): RowsState => ({ memory: 'none', asked: false });
export type RowsAction = { type: 'memory'; value: Memory } | { type: 'ask' } | { type: 'reset' };
export function rowsTransition(state: RowsState, action: RowsAction): RowsState {
  switch (action.type) {
    case 'reset':
      return initialRows();
    case 'memory':
      return { memory: action.value, asked: false };
    case 'ask':
      return { ...state, asked: true };
  }
}
export const ROWS_START_MESSAGE = 'Choose a memory, then press Ask in a new conversation.';
export const ROWS_REFINEMENT =
  'Refinement: a memory is a row the assistant writes, not the conversation.';
export function rowsSummary(state: RowsState): Summary {
  if (!state.asked) return { tone: 'neutral', message: ROWS_START_MESSAGE };
  return {
    tone: state.memory === 'none' ? 'warning' : 'success',
    message: memoryRuns[state.memory].message,
  };
}

// --- memory-recall: the person's id in the filter, the date on every row --------------------

export const PEOPLE = ['maya', 'ravi'] as const;
export type Person = (typeof PEOPLE)[number];
export const personLabels: Record<Person, string> = { maya: 'Maya', ravi: 'Ravi' };
export const toPerson = (value: string): Person => PEOPLE.find((id) => id === value) ?? 'maya';
export const STORES = ['fresh', 'stale'] as const;
export type Store = (typeof STORES)[number];
export const storeLabels: Record<Store, string> = {
  fresh: 'two fresh',
  stale: 'two fresh and one stale',
};
export const toStore = (value: string): Store => STORES.find((id) => id === value) ?? 'fresh';
export interface MemoryRow {
  person: Person;
  text: string;
  /** The created_at date as the learner reads it. */
  date: string;
  /** The same date as YYYY-MM-DD, for ordering. */
  iso: string;
  /** The subject the row is about, used to find an older row the fresh one supersedes. */
  subject: string;
}
export const row = (
  person: Person,
  text: string,
  date: string,
  iso: string,
  subject: string,
): MemoryRow => ({ person, text, date, iso, subject });
export const MAYA_NAME_ROW = row(
  'maya',
  'Wants to be called M. When listing tasks, put the task id first.',
  '6 March 2026',
  '2026-03-06',
  'how to address and list',
);
export const MAYA_PROJECT_ROW = row(
  'maya',
  'Manages the Apollo project.',
  '5 March 2026',
  '2026-03-05',
  'project',
);
export const MAYA_STALE_ROW = row(
  'maya',
  'Manages the Support project, ask about tickets first.',
  '12 February 2026',
  '2026-02-12',
  'project',
);
export const RAVI_ROW = row(
  'ravi',
  'Owns the release note draft.',
  '4 March 2026',
  '2026-03-04',
  'project',
);
export const storedRows = (store: Store): readonly MemoryRow[] =>
  store === 'fresh'
    ? [MAYA_NAME_ROW, MAYA_PROJECT_ROW, RAVI_ROW]
    : [MAYA_NAME_ROW, MAYA_PROJECT_ROW, MAYA_STALE_ROW, RAVI_ROW];
export const RECALL_QUESTION = 'List the open Apollo tasks.';
/** A row is stale when a newer row for the same person has the same subject. */
export const isStale = (entry: MemoryRow, rows: readonly MemoryRow[]) =>
  rows.some(
    (other) =>
      other !== entry &&
      other.person === entry.person &&
      other.subject === entry.subject &&
      other.iso > entry.iso,
  );
/** Recall filters by the person's id, then orders newest first. */
export const recalled = (person: Person, store: Store): readonly MemoryRow[] =>
  storedRows(store)
    .filter((entry) => entry.person === person)
    .sort((a, b) => (a.iso < b.iso ? 1 : a.iso > b.iso ? -1 : 0));
export const staleFlag = (entry: MemoryRow, store: Store) =>
  isStale(entry, storedRows(store)) ? 'stale: an older row on the same subject' : 'fresh';
export const staleCount = (person: Person, store: Store) =>
  recalled(person, store).filter((entry) => isStale(entry, storedRows(store))).length;
export interface RecallState {
  person: Person;
  store: Store;
  run: boolean;
}
export const initialRecall = (): RecallState => ({ person: 'maya', store: 'fresh', run: false });
export type RecallAction =
  | { type: 'person'; value: Person }
  | { type: 'store'; value: Store }
  | { type: 'run' }
  | { type: 'reset' };
export function recallTransition(state: RecallState, action: RecallAction): RecallState {
  switch (action.type) {
    case 'reset':
      return initialRecall();
    case 'person':
      return { ...state, person: action.value, run: false };
    case 'store':
      return { ...state, store: action.value, run: false };
    case 'run':
      return { ...state, run: true };
  }
}
export const RECALL_START_MESSAGE = 'Choose a person and the memories stored, then press Recall.';
export const MAYA_FRESH_MESSAGE =
  "Both of Maya's rows passed the id filter and reached the prompt.";
export const MAYA_STALE_MESSAGE =
  'Three rows passed the filter, and the February one is flagged by its date.';
export const RAVI_MESSAGE =
  "Ravi's turn saw his one row, and Maya's rows stayed behind the filter.";
export const RECALL_REFINEMENT =
  "Refinement: the person's id in the filter, the date on every row.";
export function recallSummary(state: RecallState): Summary {
  if (!state.run) return { tone: 'neutral', message: RECALL_START_MESSAGE };
  if (state.person === 'ravi') return { tone: 'success', message: RAVI_MESSAGE };
  return state.store === 'fresh'
    ? { tone: 'success', message: MAYA_FRESH_MESSAGE }
    : { tone: 'warning', message: MAYA_STALE_MESSAGE };
}

// --- kb-pipeline: what the ingest does when the documents change ----------------------------

export const EVENTS = ['changed', 'deleted', 'nothing'] as const;
export type Event = (typeof EVENTS)[number];
export const eventLabels: Record<Event, string> = {
  changed: 'a document changed',
  deleted: 'a document was deleted',
  nothing: 'nothing changed',
};
export const toEvent = (value: string): Event => EVENTS.find((id) => id === value) ?? 'changed';
export const DOCUMENTS = 12;
export const PIECES = 26;
export const VENUE_DOCUMENT = 'the venue email (document 2, pieces 2.1 and 2.2)';
export interface IngestRow {
  documents: string;
  pieces: number;
  did: string;
}
export interface IngestRun {
  rows: readonly IngestRow[];
  stored: number;
  skipped: number;
  leftBehind: number;
  /** The line the ingest prints. */
  output: string;
  message: string;
}
export const ingestLine = (documents: number, pieces: number) =>
  `Stored ${documents} document(s) as ${pieces} piece(s)`;
export const ingestRuns: Record<Event, IngestRun> = {
  changed: {
    rows: [
      { documents: '11 unchanged documents', pieces: 24, did: 'skipped, the pieces match' },
      { documents: VENUE_DOCUMENT, pieces: 2, did: 're-embedded and stored' },
    ],
    stored: 2,
    skipped: 24,
    leftBehind: 0,
    output: ingestLine(1, 2),
    message: 'The changed document was re-embedded, and the other twenty-four pieces were skipped.',
  },
  deleted: {
    rows: [
      { documents: '11 unchanged documents', pieces: 24, did: 'skipped, the pieces match' },
      { documents: VENUE_DOCUMENT, pieces: 2, did: 'not seen, the pieces stay in the index' },
    ],
    stored: 0,
    skipped: 24,
    leftBehind: 2,
    output: ingestLine(0, 0),
    message:
      "The ingest skipped everything it saw, and the deleted document's two pieces stayed in the index.",
  },
  nothing: {
    rows: [{ documents: '12 unchanged documents', pieces: 26, did: 'skipped, the pieces match' }],
    stored: 0,
    skipped: 26,
    leftBehind: 0,
    output: ingestLine(0, 0),
    message: 'Nothing changed, so the ingest stored nothing and the index is already true.',
  },
};
export interface PipelineState {
  event: Event;
  run: boolean;
}
export const initialPipeline = (): PipelineState => ({ event: 'changed', run: false });
export type PipelineAction = { type: 'event'; value: Event } | { type: 'run' } | { type: 'reset' };
export function pipelineTransition(state: PipelineState, action: PipelineAction): PipelineState {
  switch (action.type) {
    case 'reset':
      return initialPipeline();
    case 'event':
      return { event: action.value, run: false };
    case 'run':
      return { ...state, run: true };
  }
}
export const PIPELINE_START_MESSAGE = 'Choose an event, then press Run the ingest.';
export const PIPELINE_REFINEMENT =
  'Refinement: ingestion is a pipeline with freshness and deletion, not a folder.';
export function pipelineSummary(state: PipelineState): Summary {
  if (!state.run) return { tone: 'neutral', message: PIPELINE_START_MESSAGE };
  return {
    tone: state.event === 'deleted' ? 'warning' : 'success',
    message: ingestRuns[state.event].message,
  };
}

// --- kb-rerank: the seventeen answerable questions through three searches -----------------

export const SEARCHES = ['vector', 'rewrite', 'rerank'] as const;
export type Search = (typeof SEARCHES)[number];
export const searchLabels: Record<Search, string> = {
  vector: 'vector',
  rewrite: 'rewritten query',
  rerank: 're-ranked top twenty',
};
export const toSearch = (value: string): Search => SEARCHES.find((id) => id === value) ?? 'vector';
export const QUESTIONS = 17;
export const MISSED_QUESTION = 'q4';
export const MISSED_TEXT = 'What is blocking the launch?';
export const MISSED_PIECE = '8.1, "three things could stop the demo"';
export const MISSED_DETAIL = `${MISSED_QUESTION} "${MISSED_TEXT}", expected ${MISSED_PIECE}`;
export const REWRITTEN_QUERY = 'launch blockers';
export interface SearchRun {
  found: number;
  /** The question missed, or null when every question was found. */
  miss: string | null;
  /** The missed question and what was expected, or null when nothing was missed. */
  missDetail: string | null;
  /** Tokens for the seventeen questions, zero when no model call is added. */
  tokens: number;
  /** How the step read the missed question, or null when it adds no model call. */
  queryUsed: string | null;
  message: string;
}
export const searchRuns: Record<Search, SearchRun> = {
  vector: {
    found: 16,
    miss: MISSED_QUESTION,
    missDetail: MISSED_DETAIL,
    tokens: 0,
    queryUsed: null,
    message: 'The embedding found 16 of 17 and missed the blocking question.',
  },
  rewrite: {
    found: 16,
    miss: MISSED_QUESTION,
    missDetail: MISSED_DETAIL,
    tokens: 744,
    queryUsed: `"${REWRITTEN_QUERY}", no closer to piece 8.1`,
    message: 'The rewrite found the same 16 and missed the same question.',
  },
  rerank: {
    found: 16,
    miss: 'q9',
    missDetail:
      'q9 "How long may each speaker talk?", expected 5.2: the model\'s reply carried no piece ids',
    tokens: 15_725,
    queryUsed: 'twenty candidates ordered by the model, 8.1 moved into the top five',
    message:
      'Re-ranking found the blocking question and lost one reply that carried no ids, at about 925 tokens a question.',
  },
};
export const recallText = (run: SearchRun) => `${run.found} of ${QUESTIONS}`;
export const missText = (run: SearchRun) => run.missDetail ?? 'none';
/** Tokens per question, rounded to the nearest five, as the lesson quotes them. */
export const tokensPerQuestion = (run: SearchRun) =>
  run.tokens === 0 ? 0 : Math.round(run.tokens / QUESTIONS / 5) * 5;
export const tokensPerQuestionText = (run: SearchRun) =>
  run.tokens === 0 ? '0' : `about ${tokensPerQuestion(run)}`;
export const questionIds = Array.from({ length: QUESTIONS }, (_, index) => `q${index + 1}`);
export const questionFound = (run: SearchRun, id: string) => run.miss !== id;
export interface RerankState {
  search: Search;
  run: boolean;
}
export const initialRerank = (): RerankState => ({ search: 'vector', run: false });
export type RerankAction = { type: 'search'; value: Search } | { type: 'run' } | { type: 'reset' };
export function rerankTransition(state: RerankState, action: RerankAction): RerankState {
  switch (action.type) {
    case 'reset':
      return initialRerank();
    case 'search':
      return { search: action.value, run: false };
    case 'run':
      return { ...state, run: true };
  }
}
export const RERANK_START_MESSAGE = 'Choose a search, then press Run the seventeen questions.';
export const RERANK_REFINEMENT =
  'Refinement: measure before adding a step, and keep what moved recall.';
export function rerankSummary(state: RerankState): Summary {
  if (!state.run) return { tone: 'neutral', message: RERANK_START_MESSAGE };
  const run = searchRuns[state.search];
  return { tone: run.miss === null ? 'success' : 'warning', message: run.message };
}
